const SITE = 'https://brooklinejppatriots.com/football-practice-times';
const DIVISION_ORDER = ['7U', '9U', '11U', '13U'];

const to12h = (value) => {
  if (!value) return '';
  const [h, m] = value.split(':').map(Number);
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

export const weekStartOf = (date) => {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().slice(0, 10);
};

const longDate = (date) => new Date(`${date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

export const groupGamesByWeek = (games, today = new Date().toISOString().slice(0, 10)) => {
  const weeks = new Map();
  games.filter((g) => g.sessionType === 'game' && g.date >= today && !g.result && !g.cancelled).forEach((g) => {
    const key = weekStartOf(g.date);
    weeks.set(key, [...(weeks.get(key) || []), g]);
  });
  return [...weeks.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([weekStart, list]) => ({ weekStart, games: sortGames(list) }));
};

const sortGames = (list) => [...list].sort((a, b) => a.date.localeCompare(b.date) || DIVISION_ORDER.indexOf(a.division) - DIVISION_ORDER.indexOf(b.division) || a.endTime.localeCompare(b.endTime));

const parkingAlert = (game) => (game.notes || '').split(' · ').find((part) => /^parking/i.test(part.trim())) || '';

export const gameLine = (game) => {
  const side = /\(away\)/i.test(game.notes || '') ? ' AWAY' : /\(home\)/i.test(game.notes || '') ? ' HOME' : '';
  const matchup = game.opponent ? ` vs ${game.opponent}` : '';
  const kickoff = game.endTime ? to12h(game.endTime) : to12h(game.startTime);
  const arrival = game.endTime ? ` · arrive ${to12h(game.startTime)}` : '';
  const lines = [`${game.division || 'BJP'}${side}${matchup}: Game ${kickoff}${arrival} · ${game.location}`];
  const parking = parkingAlert(game);
  if (parking) lines.push(`⚠ ${parking.replace(/^parking:?\s*/i, 'PARKING: ')}`);
  return lines.join('\n');
};

export const buildWeeklyText = (games) => {
  if (!games.length) return '';
  const dates = [...new Set(games.map((g) => g.date))].sort();
  const heading = dates.length === 1 ? longDate(dates[0]) : `${longDate(dates[0])} – ${longDate(dates[dates.length - 1])}`;
  const byDate = dates.map((date) => {
    const dayGames = games.filter((g) => g.date === date).map(gameLine).join('\n');
    return dates.length === 1 ? dayGames : `${longDate(date).toUpperCase()}\n${dayGames}`;
  });
  return [`BJP PATRIOTS — THIS WEEK'S GAMES (${heading})`, ...byDate, `Directions, weather & live updates: ${SITE}`, 'Go Patriots!'].join('\n\n');
};

export const buildDivisionText = (games, division) => buildWeeklyText(games.filter((g) => g.division === division)).replace("THIS WEEK'S GAMES", `${division} GAME${games.filter((g) => g.division === division).length > 1 ? 'S' : ''} THIS WEEK`);

export const divisionsIn = (games) => DIVISION_ORDER.filter((d) => games.some((g) => g.division === d));


export const directionsLink = (location) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location || '')}`;

const sideOf = (game) => (/\(away\)/i.test(game.notes || '') ? 'away' : /\(home\)/i.test(game.notes || '') ? 'home' : '');
const isPlayoff = (game) => /playoff/i.test(game.notes || '');
const venueName = (location) => (location || '').split(',')[0].trim();

export const buildGameDayText = (games, division) => {
  const list = sortGames(games.filter((g) => !division || g.division === division));
  if (!list.length) return '';
  const date = list[0].date;
  const dayGames = list.filter((g) => g.date === date);
  const playoff = dayGames.some(isPlayoff);
  const who = division ? `${division} FAMILIES` : 'PATRIOTS FAMILIES';
  const header = `${who} — TOMORROW IS ${playoff ? 'PLAYOFF ' : ''}GAME DAY! (${longDate(date)})`;
  const blocks = dayGames.map((g) => {
    const side = sideOf(g);
    const lines = [
      `${g.division}${side ? ` · ${side.toUpperCase()}` : ''}${g.opponent ? ` vs ${g.opponent}` : ''}${isPlayoff(g) ? ' · PLAYOFFS' : ''}`,
      `⏰ Arrive ${to12h(g.startTime)} · Kickoff ${to12h(g.endTime || g.startTime)}`,
      `📍 ${g.location}`,
      `🧭 Directions: ${directionsLink(g.location)}`,
    ];
    const parking = parkingAlert(g);
    if (parking) lines.push(`⚠ ${parking.replace(/^parking:?\s*/i, 'PARKING: ')}`);
    return lines.join('\n');
  });
  const homeVenues = [...new Set(dayGames.filter((g) => sideOf(g) === 'home').map((g) => venueName(g.location)))];
  const reminders = ['Full game uniform, cleats, mouthguard and water. Be on time — the team warms up together.'];
  if (homeVenues.length) reminders.push(`We're the HOME team at ${homeVenues.join(' & ')} — wear navy and bring the noise!`);
  return [header, ...blocks, reminders.join('\n'), `Weather & live updates: ${SITE}`, 'Go Patriots!'].join('\n\n');
};
